import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Clock, BookOpen, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardTitle } from '../components/ui/card';
import { courseService } from '../services/course.service';
import type { Course } from '@edytech/shared/types';

const LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Specialized'];
const SORTS = [
  { value: 'newest', label: 'Newest' },
  { value: 'popular', label: 'Most Popular' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'title_asc', label: 'Title A-Z' },
] as const;

const formatGHS = (cents: number) =>
  cents === 0 ? 'Free' : `GHS ${(cents / 100).toLocaleString()}`;

const formatDuration = (minutes: number) => {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

export const CoursesPage: React.FC = () => {
  const [category, setCategory] = useState('');
  const [level, setLevel] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState<string>('newest');
  const [page, setPage] = useState(1);

  const { data: categories } = useQuery({
    queryKey: ['course-categories'],
    queryFn: () => courseService.getCategories(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['courses', { category, level, search, sort, page }],
    queryFn: () =>
      courseService.getCourses({
        category: category || undefined,
        level: level || undefined,
        search: search || undefined,
        sort: sort as never,
        page,
        limit: 12,
      }),
  });

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const applyFilter = (fn: () => void) => {
    fn();
    setPage(1);
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="mb-10 text-center">
        <h1 className="text-3xl font-bold md:text-4xl">Training Courses</h1>
        <p className="mt-2 text-[rgb(var(--light-secondary))]">
          Hands-on, expert-led training to kickstart or advance your repair career.
        </p>
      </div>

      {/* Search + Sort */}
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative flex-1 md:max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--light-secondary))]" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search courses..."
            className="w-full rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--card))] py-2.5 pl-10 pr-4 text-sm focus:outline-2 focus:outline-primary"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-[rgb(var(--light-secondary))]" />
          <select
            value={sort}
            onChange={(e) => applyFilter(() => setSort(e.target.value))}
            className="rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--card))] px-3 py-2.5 text-sm focus:outline-2 focus:outline-primary"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Category chips */}
      <div className="mb-6 flex flex-wrap gap-2">
        <button
          onClick={() => applyFilter(() => setCategory(''))}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
            !category ? 'bg-primary text-white' : 'bg-[rgb(var(--card))] border border-[rgb(var(--border))] hover:bg-[rgb(var(--border))]'
          }`}
        >
          All
        </button>
        {(categories || []).map((cat) => (
          <button
            key={cat.id}
            onClick={() => applyFilter(() => setCategory(category === cat.slug ? '' : cat.slug))}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              category === cat.slug ? 'bg-primary text-white' : 'bg-[rgb(var(--card))] border border-[rgb(var(--border))] hover:bg-[rgb(var(--border))]'
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Level filter */}
      <div className="mb-8 flex flex-wrap gap-2">
        {LEVELS.map((l) => (
          <button
            key={l}
            onClick={() => applyFilter(() => setLevel(level === l ? '' : l))}
            className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
              level === l ? 'bg-primary/10 text-primary ring-1 ring-primary' : 'text-[rgb(var(--light-secondary))] hover:bg-[rgb(var(--card))]'
            }`}
          >
            {l}
          </button>
        ))}
      </div>


      {isLoading ? (
        <div className="py-20 text-center text-[rgb(var(--light-secondary))]">Loading courses...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {(data?.data || []).map((course: Course) => (
              <Card key={course.id} className="flex flex-col overflow-hidden p-0">
                <div className="relative h-40 w-full bg-primary/10">
                  {course.coverImage ? (
                    <img src={course.coverImage} alt={course.title} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
                      <BookOpen className="h-10 w-10 text-primary" />
                    </div>
                  )}
                  {course.level && (
                    <span className="absolute left-3 top-3 rounded-full bg-black/60 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-sm">
                      {course.level}
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <CardTitle className="text-lg">{course.title}</CardTitle>
                  {course.subtitle && (
                    <p className="mt-1 line-clamp-2 text-sm text-[rgb(var(--light-secondary))]">{course.subtitle}</p>
                  )}
                  <div className="mt-3 flex items-center gap-4 text-xs text-[rgb(var(--light-secondary))]">
                    <span className="flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" />{course.lessonCount} lessons</span>
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{formatDuration(course.durationMinutes)}</span>
                  </div>
                  {course.enrollment && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-[rgb(var(--light-secondary))]">{course.enrollment.status === 'COMPLETED' ? 'Completed' : 'In progress'}</span>
                        <span className="font-medium">{course.enrollment.progressPercent}%</span>
                      </div>
                      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[rgb(var(--border))]">
                        <div className="h-full rounded-full bg-primary" style={{ width: `${course.enrollment.progressPercent}%` }} />
                      </div>
                    </div>
                  )}
                  <div className="mt-4 flex items-center justify-between pt-2">
                    <span className="text-lg font-bold">{formatGHS(course.price)}</span>
                    {course.instructorName && (
                      <span className="text-xs text-[rgb(var(--light-secondary))]">{course.instructorName}</span>
                    )}
                  </div>
                  <Link to={`/courses/${course.slug}`} className="mt-4">
                    <Button variant={course.enrollment ? 'secondary' : 'primary'} className="w-full">
                      {course.enrollment ? (course.enrollment.status === 'COMPLETED' ? 'View Course' : 'Continue') : 'View Course'}
                    </Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>

          {(!data?.data || data.data.length === 0) && (
            <div className="py-16 text-center text-[rgb(var(--light-secondary))]">
              No courses match your filters.
            </div>
          )}

          {data?.pagination && data.pagination.totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2">
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <ChevronLeft className="h-4 w-4" /> Prev
              </Button>
              <span className="px-4 text-sm text-[rgb(var(--light-secondary))]">
                Page {data.pagination.page} of {data.pagination.totalPages} ({data.pagination.total} courses)
              </span>
              <Button variant="secondary" size="sm" disabled={page >= data.pagination.totalPages} onClick={() => setPage((p) => p + 1)}>
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
